'use strict';

// ─────────────────────────────────────────────────────────────────────────────
// Metafield-driven flavour picker.
//
// Quota and choices are re-read from the SELECTED variant on every variant:change:
//   custom.bag_count    → how many flavours this pack holds (the hard cap)
//   custom.flavours     → which variants may be picked (list.variant_reference)
//   custom.allow_addons → the product type, which sets BOTH the limit and the cart shape:
//                         false = capped at bag_count, true = uncapped with a minimum of one
//
// A variant with no config is simply not a bundle: the drawer never opens and
// add-to-cart runs untouched.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The two cart shapes the Shopify Function understands, chosen by custom.allow_addons.
 *
 * allow_addons = false → Model A: ONE pack line. The picks ride along as "id:qty,id:qty" — the format the
 *   Cart Transform parses — and their quantities must sum to bag_count.
 *
 * allow_addons = true  → Model B: one line PER flavour, each billed separately, tied together by
 *   _addon = "group:parent:perPack" (fresh group id per add) plus a readable _bundle_title. The parent
 *   only names the merged row and is never added as its own line — except on a custom.bundle product,
 *   whose default variant parents the box and may itself be picked.
 *
 * @param {{allowAddons: boolean, parentId?: number, bundleTitle?: string}} entry  config for the selected variant
 * @param {number} parentVariantId          the selected variant: Model A's pack line, Model B's parent unless entry.parentId
 * @param {Array<[number, number]>} picks   [[flavourVariantId, quantity], …]
 * @param {number} packQuantity             how many packs (multiplies both models)
 * @param {string} groupId                  unique per add; only used by Model B
 */
function buildCartItems(entry, parentVariantId, picks, packQuantity, groupId) {
  if (!entry.allowAddons) {
    return [
      {
        id: parentVariantId,
        quantity: packQuantity,
        properties: { _bundle_picks: picks.map(([id, qty]) => `${id}:${qty}`).join(',') },
      },
    ];
  }

  // N packs are one _addon group of N× each flavour. _addon = "group:parent:perPack" — one property,
  // since every line property reaches the order. perPack makes the merged row's quantity the pack
  // count, so its +/- steps whole packs, not ×2. The parent is the entry's: on a custom.bundle product
  // the default variant, which may itself be picked. _bundle_title is the readable order line.
  const parent = entry.parentId || parentVariantId;
  return picks.map(([variantId, quantity]) => ({
    id: variantId,
    quantity: quantity * packQuantity,
    // _bundle_title first: the order lists properties in the order they were sent.
    properties: {
      ...(entry.bundleTitle && { _bundle_title: entry.bundleTitle }),
      _addon: `${groupId}:${parent}:${quantity}`,
    },
  }));
}

/**
 * The selection rules, switched by the same custom.allow_addons that picks the cart shape:
 *   false → exactly bagCount flavours (capped packs)
 *   true  → at least one flavour, no ceiling
 */
function isComplete(entry, total, picks) {
  if (!entry) return false;
  if (unmetMinimums(entry, picks).length > 0) return false;
  return entry.allowAddons ? total >= 1 : entry.bagCount > 0 && total === entry.bagCount;
}

/**
 * Flavours whose custom.flavours_metadata minimum_count is not satisfied yet.
 *
 * Minimums are a hard floor and are NOT pre-filled, so a box can read 4/4 and still be un-addable —
 * this list is what the footer turns into a reason, and what isComplete gates on.
 *
 * Sold-out flavours are exempt: their row has no stepper, so a minimum on one would be impossible to
 * meet and would take the whole product off sale with no way for the customer to act on it.
 */
function unmetMinimums(entry, picks) {
  if (!entry) return [];
  return entry.flavours.filter(
    (f) => f.available && f.min > 0 && (picks.get(String(f.id)) || 0) < f.min
  );
}

/**
 * How high one row may still be stepped: the lower of what the box has left and this flavour's own
 * maximum_count. A null max means the metaobject set no ceiling, so only the box constrains it.
 */
function pickCeiling(entry, flavour, totalOfOtherRows) {
  if (!entry) return 0;
  const boxCeiling = entry.allowAddons ? Infinity : entry.bagCount - totalOfOtherRows;
  const flavourCeiling = flavour && flavour.max != null ? flavour.max : Infinity;
  return Math.min(boxCeiling, flavourCeiling);
}

/**
 * The volume-discount tier the picked bag count has reached, and the next one up. Tiers arrive in
 * whatever order the shop manager added the metaobjects, and an incomplete one (no bags or no
 * amount) is dropped rather than promising "save 0 €".
 */
function volumeTierState(tiers, picked) {
  const sorted = (tiers || []).filter((t) => t.bags > 0 && t.off > 0).sort((a, b) => a.bags - b.bags);
  return {
    reached: sorted.filter((t) => picked >= t.bags).pop() || null,
    next: sorted.find((t) => t.bags > picked) || null,
  };
}

/** Sum of the picked flavours' prices in cents — allow_addons bills one line per flavour at its own price. */
function pickedTotal(entry, picks) {
  return entry.flavours.reduce((sum, f) => sum + (f.price || 0) * (picks.get(String(f.id)) || 0), 0);
}

// A fresh id per box: Date.now() alone collides when two boxes are added inside the same
// millisecond, and a reused id merges two boxes into one row.
function newGroupId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// Tier celebration, modelled on metaflow.de's step indicator: the "tier reached" text holds for 3s, then
// the normal message comes back.
const CELEBRATION_MS = 3000;

class FlavourPickerController {
  constructor(drawerEl) {
    this._el = drawerEl;
    this._cfg = null;
    this._entry = null;
    this._variantId = null;
    this._picks = new Map();
    this._btn = null; // last button state + text, so _render knows what to roll
    this._lastReachedBags = 0; // tier the last render had reached, so a crossing can be celebrated once
    this._celebration = null; // { bags, off, timer } while the celebration text is up
  }

  init() {
    const configEl = this._el.querySelector('script[data-flavour-config]');
    if (!configEl) return;

    try {
      this._cfg = JSON.parse(configEl.textContent);
    } catch (e) {
      // Liquid generates this and nothing lints it before it ships, so print the whole config.
      console.error('[FlavourPicker] Invalid config JSON:', e, configEl.textContent);
      return;
    }

    this._wireEvents();
    this._sync(this._currentVariantId());

    if (this._el._openWhenReady) {
      this._el._openWhenReady = false;
      this._el.show();
    }
  }

  // Queried fresh every time: product:rerender replaces the form's hidden input, so a cached
  // reference goes stale the first time the customer switches pack size.
  _form() {
    return document.querySelector(`form[id^="product-form-${this._cfg.productId}-"]`);
  }

  _currentVariantId() {
    const input = this._form()?.querySelector('[name="id"]');
    return input?.value || Object.keys(this._cfg.variants)[0] || null;
  }

  _packQuantity() {
    const qty = Number(this._form()?.querySelector('[name="quantity"]')?.value);
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  get quota() {
    return this._entry ? this._entry.bagCount : 0;
  }

  /** custom.allow_addons products have no bag_count to cap against: any number of flavours, minimum one. */
  get unlimited() {
    return Boolean(this._entry && this._entry.allowAddons);
  }

  /** _render and _onConfirm both ask this one, so an enabled button and an accepted submit can never
   *  disagree about the rule. */
  _isComplete() {
    return isComplete(this._entry, this._total(), this._picks);
  }

  _flavour(variantId) {
    return this._entry ? this._entry.flavours.find((f) => String(f.id) === String(variantId)) : null;
  }

  // ── Events ───────────────────────────────────────────────────────────────

  _wireEvents() {
    this._el.addEventListener('click', (e) => {
      const stepBtn = e.target.closest('[data-bundle-step]');
      if (stepBtn) {
        e.preventDefault();
        const row = stepBtn.closest('[data-variant-id]');
        if (row) this._step(row.dataset.variantId, stepBtn.dataset.bundleStep === 'inc' ? 1 : -1);
        return;
      }

      const confirmBtn = e.target.closest('[data-bundle-confirm]');
      if (confirmBtn && !confirmBtn.disabled) {
        e.preventDefault();
        this._onConfirm();
      }
    });

    // Two signals: variant:change lands only after custom-variant-picker's section fetch and is
    // skipped when that fetch is aborted or superseded, while #syncForm's change on the hidden
    // [name="id"] fires synchronously on every selection. Both re-read the form, never the event.
    document.addEventListener('variant:change', () => this._sync(this._currentVariantId()));

    document.addEventListener('change', (e) => {
      const input = e.target;
      if (!(input instanceof HTMLInputElement) || input.name !== 'id') return;
      // Ignore another product's form (a quick-add card, a second PDP form).
      const form = this._form();
      if (form && input.form && input.form !== form) return;
      this._sync(this._currentVariantId());
    });
  }

  // ── State ────────────────────────────────────────────────────────────────

  /** Point the drawer at a variant. Picks reset on a real change — a different pack means a different
   *  quota and possibly a different flavour list, so carrying them over would silently exceed
   *  bag_count.
   *
   *  Re-selecting the SAME variant is not a change and must not clear anything: both signals above
   *  fire for one selection, and a customer part-way through a box would otherwise watch their picks
   *  vanish when the second one landed. */
  _sync(variantId) {
    const next = variantId == null ? null : String(variantId);
    if (next === this._variantId) return;

    const prev = this._entry;
    this._variantId = next;
    this._entry = (this._variantId && this._cfg.variants[this._variantId]) || null;

    // custom.bundle products give every variant the same entry (one shared parent): nothing to reset.
    if (prev?.parentId != null && prev.parentId === this._entry?.parentId) return;

    this._picks.clear();
    this._btn = null; // a different pack starts still — nothing rolls in from the last one
    this._renderRows();
    this._render();
  }

  _step(variantId, delta) {
    const current = this._picks.get(variantId) || 0;
    const others = this._total() - current;
    const ceiling = pickCeiling(this._entry, this._flavour(variantId), others);
    const next = Math.max(0, Math.min(current + delta, ceiling));

    if (next === 0) this._picks.delete(variantId);
    else this._picks.set(variantId, next);

    this._render();
    this._announce(variantId);
  }

  _total() {
    return [...this._picks.values()].reduce((a, b) => a + b, 0);
  }

  // ── Render ───────────────────────────────────────────────────────────────

  _renderRows() {
    const list = this._el.querySelector('[data-bundle-list]');
    if (!list) return;

    const size = this._cfg.imageSize || 64;
    list.innerHTML = '';
    if (!this._entry) return;

    for (const flavour of this._entry.flavours) {
      const li = document.createElement('li');
      li.className = 'custom-bundle-picker__row';
      if (!flavour.available) li.classList.add('custom-bundle-picker__row--unavailable');
      li.dataset.variantId = String(flavour.id);

      const label = this._escHtml(flavour.label);
      const named = (key, fallback) =>
        this._escHtml((this._el.dataset[key] || fallback).replace('NAME', flavour.label));
      const inner = flavour.image
        ? `<img class="custom-bundle-picker__image" src="${this._escHtml(flavour.image)}" alt="${label}" width="${size}" height="${size}" loading="lazy">`
        : '<div class="custom-bundle-picker__image-placeholder"></div>';

      const control = flavour.available
        ? `<div class="custom-bundle-picker__stepper" role="group" aria-label="${label}">
             <button type="button" class="custom-bundle-picker__stepper-btn custom-bundle-picker__stepper-btn--dec" data-bundle-step="dec" disabled aria-label="${named('removeTemplate', 'Remove NAME')}">−</button>
             <span class="custom-bundle-picker__stepper-value" data-bundle-value aria-live="polite">0</span>
             <button type="button" class="custom-bundle-picker__stepper-btn custom-bundle-picker__stepper-btn--inc" data-bundle-step="inc" aria-label="${named('addTemplate', 'Add NAME')}">+</button>
           </div>`
        : `<span class="custom-bundle-picker__unavailable">${this._escHtml(this._el.dataset.unavailableLabel || '')}</span>`;

      // Colour keys read exactly as the metaobject spells them (textColour / backgroundColor), so a
      // typo shows as an unstyled badge rather than a silent default.
      const badges = (flavour.labels || [])
        .filter((b) => b && b.value)
        .map((b) => {
          const style = `${b.backgroundColor ? `background:${b.backgroundColor};` : ''}${b.textColour ? `color:${b.textColour}` : ''}`;
          return `<span class="custom-bundle-picker__badge" style="${this._escHtml(style)}">${this._escHtml(b.value)}</span>`;
        })
        .join('');

      li.innerHTML = `
        <div class="custom-bundle-picker__image-wrap" style="width:${size}px;height:${size}px">${inner}</div>
        <span class="custom-bundle-picker__name">
          ${badges ? `<span class="custom-bundle-picker__badges">${badges}</span>` : ''}
          <span class="custom-bundle-picker__name-text">${label}</span>
        </span>
        ${control}
      `;
      list.appendChild(li);
    }
  }

  _render() {
    const el = this._el;
    const quota = this.quota;
    const total = this._total();

    // Both strings carry a QTY placeholder that has no value when uncapped, so drop it along with the
    // "/" that separates it — "3 selected", not "3/0 selected". Collapsing whitespace afterwards keeps
    // locales that put QTY mid-sentence ("Pick your QTY flavours") from rendering a double space.
    const titleEl = el.querySelector('[data-bundle-title]');
    if (titleEl && el.dataset.titleTemplate) {
      titleEl.textContent = this.unlimited
        ? el.dataset.titleTemplate.replace('QTY', '').replace(/\s+/g, ' ').trim()
        : el.dataset.titleTemplate.replace('QTY', String(quota));
    }

    const counterEl = el.querySelector('[data-bundle-counter]');
    if (counterEl) {
      const tmpl = el.dataset.counterTemplate || 'SEL/QTY';
      counterEl.textContent = this.unlimited
        ? tmpl.replace('/QTY', '').replace('SEL', String(total)).replace(/\s+/g, ' ').trim()
        : tmpl.replace('SEL', String(total)).replace('QTY', String(quota));
    }

    el.querySelectorAll('[data-variant-id]').forEach((row) => {
      const count = this._picks.get(row.dataset.variantId) || 0;

      const valueEl = row.querySelector('[data-bundle-value]');
      const decBtn = row.querySelector('[data-bundle-step="dec"]');
      const incBtn = row.querySelector('[data-bundle-step="inc"]');

      if (valueEl) valueEl.textContent = String(count);
      if (decBtn) decBtn.disabled = count === 0;
      // Stop at whichever runs out first: this flavour's maximum_count, or the box's remaining room.
      if (incBtn) {
        const ceiling = pickCeiling(this._entry, this._flavour(row.dataset.variantId), total - count);
        incBtn.disabled = count >= ceiling;
      }

      row.classList.toggle('is-selected', count > 0);
    });

    // Minimums are not pre-filled, so "4/4" can still be un-addable. Name what is missing, or the
    // customer is left pressing a dead button.
    const hintEl = el.querySelector('[data-bundle-hint]');
    if (hintEl && el.dataset.minimumText && this.unlimited) {
      // The merchant's own line ("Wähle min. 1 Sorte aus"), as typed, until the box can be added.
      // allow_addons only: a capped pack falls through to the per-flavour minimums below.
      hintEl.textContent = el.dataset.minimumText;
      hintEl.hidden = this._isComplete();
    } else if (hintEl) {
      const missing = unmetMinimums(this._entry, this._picks);
      const tmpl = el.dataset.minimumTemplate || 'At least QTY × NAME';
      hintEl.textContent = missing
        .map((f) => tmpl.replace('QTY', String(f.min)).replace('NAME', f.label))
        .join(', ');
      hintEl.hidden = missing.length === 0;
    }

    this._renderVolume(total);

    const confirmBtn = el.querySelector('[data-bundle-confirm]');
    if (confirmBtn) {
      const incomplete = !this._isComplete();
      confirmBtn.disabled = incomplete;
      confirmBtn.setAttribute('aria-disabled', String(incomplete));
      // allow_addons: section texts (blank = locale), one while nothing is picked, one with the count
      // ("1 Beutel …"). A capped pack keeps the plain "Auswahl bestätigen" — no count, no other text.
      const state = !this.unlimited ? 'cta' : total > 0 ? 'count' : 'empty';
      const label = { cta: el.dataset.ctaLabel, count: el.dataset.confirmTemplate, empty: el.dataset.confirmEmptyTemplate }[state];
      if (label) {
        // Same state: only the count rolls ("1 Beutel" → "2 Beutel"). State change (nothing picked ↔ picked):
        // the whole label rolls. Mirrors the metaflow.de configurator this was modelled on.
        // Untouched when nothing changed, so a re-render (the celebration ending) can't cut a roll short.
        const plain = this._escHtml(label).replace('NEED', () => String(total));
        const last = this._btn;
        if (!last) confirmBtn.innerHTML = plain;
        else if (last.state !== state) confirmBtn.innerHTML = this._roll(last.plain, plain);
        else if (last.plain !== plain) confirmBtn.innerHTML = this._escHtml(label).replace('NEED', () => this._roll(last.count, String(total)));
        this._btn = { state, plain, count: String(total) };
      }
    }
  }

  /** Volume-discount message and running total. allow_addons products only: a capped pack is one line
   *  at the pack's own price, so neither a per-bag tier nor a sum of flavour prices describes it. */
  _renderVolume(total) {
    const el = this._el;
    const volumeEl = el.querySelector('[data-bundle-volume]');
    const totalEl = el.querySelector('[data-bundle-total]');
    const addons = this.unlimited;
    // Nothing picked yet: the minimum hint speaks alone; offers start with the first bag.
    const { reached, next } = addons && total > 0 ? volumeTierState(this._cfg.volumeTiers, total) : {};

    // A pick that crosses into a new tier celebrates it; dropping back below that tier ends it early.
    const reachedBags = reached ? reached.bags : 0;
    if (reachedBags > this._lastReachedBags && el.dataset.volumeCelebrationTemplate) this._celebrate(reached);
    else if (this._celebration && reachedBags < this._celebration.bags) this._endCelebration();
    this._lastReachedBags = reachedBags;

    if (volumeEl) {
      // Static on purpose: only the button count and the total roll (_roll), the message just updates.
      const fill = (tmpl, off, need) =>
        this._escHtml(tmpl || '')
          .replace('OFF', () => `<strong>${this._escHtml(this._money(off))}</strong>`)
          .replace('NEED', () => `<strong>${need}</strong>`);
      const need = next ? next.bags - total : 0;
      const parts = [];
      if (this._celebration) {
        parts.push(fill(el.dataset.volumeCelebrationTemplate, this._celebration.off));
      } else {
        // The current saving only speaks once there is no next tier to point at.
        if (reached && !next) parts.push(fill(el.dataset.volumeReachedTemplate, reached.off));
        if (next) {
          const tmpl = need === 1 ? el.dataset.volumeNextOneTemplate : el.dataset.volumeNextTemplate;
          parts.push(fill(tmpl, next.off, need));
        }
      }
      volumeEl.innerHTML = parts.join(' ');
      volumeEl.hidden = parts.length === 0;
    }

    if (totalEl) {
      const gross = addons ? pickedTotal(this._entry, this._picks) : 0;
      const off = reached ? Math.min(reached.off, gross) : 0;
      const amount = this._moneySplit(gross - off);
      // The whole <s> rolls, not just its text: line-through doesn't reach into the roll's inline-block.
      const compare = gross > 0 && off
        ? `<s class="custom-bundle-picker-drawer__total-compare">${this._escHtml(this._money(gross, true))}</s>`
        : undefined;
      if (amount !== this._lastTotal || compare !== this._lastCompare) {
        totalEl.innerHTML =
          gross > 0
            ? this._roll(this._lastTotal, amount) +
              (compare
                ? ` ${this._roll(this._lastCompare, compare)}` +
                  `<span class="custom-bundle-picker-drawer__total-saving">-${this._escHtml(this._money(off))}</span>`
                : '')
            : '';
      }
      totalEl.hidden = gross === 0;
      // Cleared when hidden, so the next box (or tier) doesn't roll in from a stale value.
      this._lastTotal = gross > 0 ? amount : undefined;
      this._lastCompare = compare;
    }
  }

  /** Hold the "tier reached" text for CELEBRATION_MS; the re-render after it brings back the
   *  normal reached / next-tier message. A second tier inside the window restarts it with the new amount. */
  _celebrate(tier) {
    this._endCelebration();
    const timer = setTimeout(() => {
      this._celebration = null;
      this._render();
    }, CELEBRATION_MS);
    this._celebration = { bags: tier.bags, off: tier.off, timer };
  }

  _endCelebration() {
    if (!this._celebration) return;
    clearTimeout(this._celebration.timer);
    this._celebration = null;
  }

  /** Locale-formatted money from cents. Whole amounts drop the decimals ("5 €") unless `cents` is forced. */
  _money(amount, cents = false) {
    return this._moneyFormat(amount, cents).format(amount / 100);
  }

  /** "24,47 €" with everything from the decimal separator on in a smaller span, as the PDP price shows it. */
  _moneySplit(amount) {
    let whole = '';
    let rest = '';
    for (const part of this._moneyFormat(amount, true).formatToParts(amount / 100)) {
      if (part.type === 'decimal' || rest) rest += part.value;
      else whole += part.value;
    }
    return `${this._escHtml(whole)}<span class="custom-bundle-picker-drawer__total-decimal">${this._escHtml(rest)}</span>`;
  }

  _moneyFormat(amount, cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'de', {
      style: 'currency',
      currency: (this._cfg.dataLayer && this._cfg.dataLayer.currency) || 'EUR',
      minimumFractionDigits: cents || amount % 100 ? 2 : 0,
    });
  }

  /** Old value up and out, then the new one down into place (CSS in custom-flavour-picker.liquid). Nothing
   *  to roll from, or nothing changed, renders `next` as-is. Both arguments must already be escaped. */
  _roll(prev, next) {
    if (prev === undefined || prev === next) return next;
    return (
      '<span class="custom-flavour-picker__roll">' +
      `<span class="custom-flavour-picker__roll-out" aria-hidden="true">${prev}</span>` +
      `<span class="custom-flavour-picker__roll-in">${next}</span>` +
      '</span>'
    );
  }

  _escHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // ── Cart submission ──────────────────────────────────────────────────────

  async _onConfirm() {
    // The drawer closes before the add resolves, and the PDP's add-to-cart reopens it with the picks
    // still set — without this a second confirm posts the same box again (ProductForm's #submitting).
    if (this._submitting || !this._entry || !this._isComplete()) return;

    // Last line of defence before money moves: if the form's variant drifted from the one these
    // picks were made against (a missed variant:change, a rerender), re-sync instead of charging
    // for the wrong pack. _sync clears the picks, which is correct — they belong to the old quota.
    const live = this._form()?.querySelector('[name="id"]')?.value;
    if (live && live !== this._variantId) {
      this._sync(live);
      this._el.show();
      return;
    }

    const parent = Number(this._variantId);
    const picks = [...this._picks].map(([id, qty]) => [Number(id), qty]);
    const items = buildCartItems(this._entry, parent, picks, this._packQuantity(), newGroupId());

    this._el.hide();
    this._submitting = true;

    try {
      const res = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || `HTTP ${res.status}`);

      this._fireAddedToCart(data.items || []);
      this._picks.clear();
      this._render();
      this._refreshCartDrawer();
    } catch (err) {
      console.error('[FlavourPicker] cart/add failed:', err.message);
      // Reopen so the customer sees their picks still there rather than a silently dead button.
      this._el.show();
    } finally {
      this._submitting = false;
    }
  }

  _fireAddedToCart(items) {
    const dl = this._cfg.dataLayer;
    if (!dl || items.length === 0) return;

    const value = items.reduce((sum, item) => sum + item.final_line_price, 0) / 100;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(
      {
        ecommerce: {
          currency: dl.currency,
          value: value.toFixed(2),
          items: items.map((item) => ({
            item_id: item.sku,
            item_variant: item.variant_id,
            item_category: item.product_type || dl.itemCategory,
            item_name: item.product_title || dl.itemName,
            price: (item.price / 100).toFixed(2),
            quantity: item.quantity,
            brand: dl.brand,
          })),
        },
      },
      { event: 'product_added_to_cart' }
    );
  }

  // cart:refresh is the theme's own path: CartDrawer refetches its section and replaces its children,
  // and theme.js refreshes the cart promise and count off the same event.
  _refreshCartDrawer() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.querySelector('cart-drawer')?.show();
  }

  // ── Accessibility ─────────────────────────────────────────────────────────

  _announce(variantId) {
    const announceEl = this._el.querySelector('[data-bundle-announce]');
    if (!announceEl) return;

    const flavour = this._flavour(variantId);
    const count = this._picks.get(variantId) || 0;
    const progress = this.unlimited ? `${this._total()}` : `${this._total()}/${this.quota}`;
    announceEl.textContent = `${flavour ? flavour.label : variantId}: ${count}. ${progress}`;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Element registration + add-to-cart intercept.
// ─────────────────────────────────────────────────────────────────────────────
if (!customElements.get('custom-flavour-picker-drawer')) {
  customElements.whenDefined('x-drawer').then(() => {
    class CustomFlavourPickerDrawer extends customElements.get('x-drawer') {
      // Prevents MutationObserver duplicates.
      get shouldAppendToBody() {
        return false;
      }

      connectedCallback() {
        super.connectedCallback();
        if (this._flavourController) return;

        this._flavourController = new FlavourPickerController(this);
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this._flavourController.init(), { once: true });
        } else {
          this._flavourController.init();
        }
      }
    }

    customElements.define('custom-flavour-picker-drawer', CustomFlavourPickerDrawer);
  });
}

// Opens the drawer instead of submitting, whenever the selected variant is a bundle. Registered
// immediately (not on DOMContentLoaded) and in the capture phase so clicks during page load are
// caught before theme.js handlers run. Confirming inside the drawer posts to cart/add.js itself,
// so the form submit is never the path that adds a bundle.
(function setupFlavourTriggerIntercept() {
  const drawer = () => document.querySelector('custom-flavour-picker-drawer');
  let warnedFor;

  // sticky-atc's button is type="button" and posts the bare variant itself, so it must open the drawer too.
  const SELECTOR =
    'button[type="submit"].button--atc, buy-buttons button[type="submit"], .product-form [type="submit"], ' +
    'sticky-atc .sticky-atc__button-row button';

  document.addEventListener(
    'click',
    (e) => {
      const btn = e.target.closest(SELECTOR);
      if (!btn) return;

      const d = drawer();
      if (!d) return;

      const ctrl = d._flavourController;

      // Controller not ready yet — block and queue, so the loading window can't leak a bare add.
      // Deliberately before the form check below: without _cfg there is no way to tell whose form
      // this is, and blocking a click is cheaper than selling a bare pack.
      if (!ctrl?._cfg) {
        e.preventDefault();
        e.stopImmediatePropagation();
        d._openWhenReady = true;
        return;
      }

      // The listener is on document, so it sees every add-to-cart on the page — a Featured product
      // section also renders <buy-buttons>. button.form resolves the owning form natively (ancestor
      // or form= attribute); anything that is not OUR product's form is somebody else's sale.
      if (btn.form && ctrl._form() && btn.form !== ctrl._form()) return;

      // Not a bundle variant: add-to-cart runs untouched. Name the variant and the ones that ARE
      // configured, so a metafield typo costs one console line rather than another page reload.
      if (!ctrl._entry) {
        if (warnedFor !== ctrl._variantId) {
          warnedFor = ctrl._variantId;
          console.warn(
            `[FlavourPicker] Variant ${ctrl._variantId} has no flavours/bag_count/allow_addons config - adding ` +
              `to cart without the picker. Configured: ${Object.keys(ctrl._cfg.variants).join(', ') || '(none)'}`
          );
        }
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();
      d.show();
    },
    true
  );
})();
